import './PartialReverse.scss';

import { Button, Chip, Chips } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC } from 'react';
import { useState } from 'react';
import { useAppSelector } from 'store/hooks';
import type {
  PayHubCashierContractsPendingWithdrawalResponse,
  PayHubCashierContractsReversePendingWithdrawalV2Item,
} from 'store/models';
import { currencyDisplaySelector } from 'store/slices/deposit';
import { getFormattedAmountWithCurrency } from 'utils';

import { ReverseHeader } from '../ReverseHeader';
import { FULL_REVERSE_PERCENT } from '../utils';
import { PendingWithdrawalCard } from './PendingWithdrawalCard';
import {
  calculateReverseAmounts,
  calculateTotalReturn,
  getAvailableOrderIds,
  getAvailablePercents,
  getPreselectedPercent,
  isPercentAvailable,
} from './utils';

interface PartialReverseProps {
  withdrawals: PayHubCashierContractsPendingWithdrawalResponse[];
  preselectedOption: number;
  precision?: number;
  isFetching: boolean;
  onReturn: (items: PayHubCashierContractsReversePendingWithdrawalV2Item[]) => void;
  onDeposit: () => void;
}

export const PartialReverse: FC<PartialReverseProps> = ({
  withdrawals,
  preselectedOption,
  precision,
  isFetching,
  onReturn,
  onDeposit,
}) => {
  const { translate } = useSkeletonTranslation();
  const currencyDisplaySetting = useAppSelector(currencyDisplaySelector);
  // single-currency list is guaranteed by BE validation, so the first currency is the popup currency
  const currency = withdrawals[0]?.currency;
  const availablePercents = getAvailablePercents(withdrawals);

  const [selectedPercent, setSelectedPercent] = useState<number | null>(() =>
    getPreselectedPercent(preselectedOption, withdrawals),
  );
  const [checkedIds, setCheckedIds] = useState<number[]>(() =>
    selectedPercent === null ? [] : getAvailableOrderIds(withdrawals, selectedPercent),
  );

  const formatAmount = (amount: number) =>
    getFormattedAmountWithCurrency(`${amount}`, currency, currencyDisplaySetting);

  // min_limit is absent when the method is gone or has no limit — then the text has no limit value
  const getBelowLimitText = (remainAmount: number, minLimit?: number | null) =>
    minLimit
      ? translate('PH.DEPOSIT.PARTIAL_REVERSE.BELOW_LIMIT_WITH_VALUE', {
          remain: formatAmount(remainAmount),
          limit: formatAmount(minLimit),
          defaultValue: "Remaining amount ({{remain}}) is below the method's limit of {{limit}}.",
        })
      : translate('PH.DEPOSIT.PARTIAL_REVERSE.BELOW_LIMIT', {
          defaultValue: "Remaining amount is below the method's limit.",
        });

  // BRD AC-1: picking a % checks every withdrawal valid for it, including ones re-enabled by the new %
  const selectPercent = (percent: number) => {
    setSelectedPercent(percent);
    setCheckedIds(getAvailableOrderIds(withdrawals, percent));
  };

  const toggleWithdrawal = (orderId: number) =>
    setCheckedIds((ids) => (ids.includes(orderId) ? ids.filter((id) => id !== orderId) : [...ids, orderId]));

  const totalReturn =
    selectedPercent === null ? 0 : calculateTotalReturn(withdrawals, checkedIds, selectedPercent, precision);
  const isReturnDisabled = selectedPercent === null || checkedIds.length === 0 || isFetching;

  const handleReturn = () => {
    if (selectedPercent === null) {
      return;
    }

    onReturn(checkedIds.map((orderId) => ({ order_id: orderId, reverse_percent: selectedPercent })));
  };

  return (
    <div className="partial-reverse">
      <ReverseHeader
        title={translate('PH.DEPOSIT.PARTIAL_REVERSE.TITLE', { defaultValue: 'GET MONEY BACK' })}
        subtitle={translate('PH.DEPOSIT.PARTIAL_REVERSE.BODY', {
          defaultValue: 'Choose the amount you want to instantly return. The rest will remain in the withdrawal',
        })}
      >
        <Chips
          dataTestId="partial-reverse-chips"
          className="partial-reverse__chips"
          selectedValues={selectedPercent === null ? [] : [selectedPercent]}
        >
          {availablePercents.map((percent) => (
            <Chip
              key={percent}
              value={percent}
              dataTestId={`partial-reverse-chip-${percent}`}
              checked={selectedPercent === percent}
              onClick={() => selectPercent(percent)}
            >
              {percent === FULL_REVERSE_PERCENT
                ? translate('PH.DEPOSIT.PARTIAL_REVERSE.ALL', { defaultValue: 'All' })
                : `${percent}%`}
            </Chip>
          ))}
        </Chips>
      </ReverseHeader>

      <div className="partial-reverse__list">
        {withdrawals.map((withdrawal) => {
          const isDisabled = selectedPercent !== null && !isPercentAvailable(withdrawal, selectedPercent);
          // disabled cards still show the split for the selected %, the error explains why it is not allowed
          const amounts =
            selectedPercent === null ? null : calculateReverseAmounts(withdrawal.amount, selectedPercent, precision);

          return (
            <PendingWithdrawalCard
              key={withdrawal.order_id}
              orderId={withdrawal.order_id}
              logo={withdrawal.logo}
              name={withdrawal.pay_group_name}
              amount={formatAmount(withdrawal.amount)}
              returnAmount={amounts ? formatAmount(amounts.returnAmount) : undefined}
              remainAmount={amounts ? formatAmount(amounts.remainAmount) : undefined}
              checked={!isDisabled && checkedIds.includes(withdrawal.order_id)}
              disabled={isDisabled}
              errorText={
                isDisabled && amounts ? getBelowLimitText(amounts.remainAmount, withdrawal.min_limit) : undefined
              }
              onToggle={toggleWithdrawal}
            />
          );
        })}
      </div>

      <div className="partial-reverse__buttons">
        <Button variant="deposit" disabled={isReturnDisabled} onClick={handleReturn}>
          {totalReturn > 0
            ? translate('PH.BUTTON.PARTIAL_REVERSE', {
                amount: formatAmount(totalReturn),
                defaultValue: 'Return {{amount}}',
              })
            : translate('PH.BUTTON.PARTIAL_REVERSE_EMPTY', { defaultValue: 'Return' })}
        </Button>
        <Button variant="secondary" onClick={onDeposit}>
          {translate('CASHIER.DEPOSIT')}
        </Button>
      </div>
    </div>
  );
};
